import { ReactNode } from "react";
import { IonButton, IonContent, IonPage, IonText, useIonModal } from "@ionic/react";
import { useQuery } from "@tanstack/react-query";
import {
  Chip,
  type ChipCancelledStatus,
  type ChipCompletedStatus,
  type ChipFilledDisplay,
  type ChipPendingStatus,
  type ChipUnfilledStatus,
  type ChipWaitlistStatus,
} from "components";
import { FeedbackModel, IntervalModel, SchoolModel } from "model";
import { useAuth } from "pages";
import { subPhotoPlaceholderPNG } from "swing-assets";
import { Card, Icon } from "swing-components";
import { phoneNumberFormatter, phoneNumberParser } from "swing-utils";

import { fetchSubPii, translate } from "~utils";

import { AdminExperience } from "../AdminExperience/AdminExperience";
import { type SubLastFeedback } from "../AdminExperience/GiveFeedback/GiveFeedback";
import styles from "./SubInfoCard.module.css";

/***** Types *****/
type FilledOrCompletedStatus = {
  status: "FILLED" | "COMPLETED";
  sub: {
    id: string;
    profileImageUrl?: string;
    name: string;
    onSubNameClick: () => void;
    phoneNumber?: string;
    email?: string;
    isSwingHero?: boolean;
  };
  school: SchoolModel;
  intervals: IntervalModel[];
  requestId: string;
  display: ChipFilledDisplay;
  hasFeedback?: boolean;
  feedback?: FeedbackModel[];
  subLastFeedback?: SubLastFeedback;
};

type UnfilledStatus = ChipUnfilledStatus;
type CompletedStatus = ChipCompletedStatus;
type CancelledStatus = ChipCancelledStatus & {
  reason: string;
  intervals: IntervalModel[]; // cancelled can have issues reported for multiday
  requestId: string;
  sub?: {
    id: string;
    name: string;
  };
  school: SchoolModel;
  hasFeedback?: boolean;
  feedback?: FeedbackModel[];
  subLastFeedback?: SubLastFeedback;
};

type WaitListStatus = ChipWaitlistStatus & {
  isChooseSub?: boolean;
  children?: ReactNode;
};

type PendingStatus = Pick<ChipPendingStatus, "status"> & {
  sub: {
    name: string;
    onSubNameClick: () => void;
    profileImageUrl?: string;
  };
};

type SubInfoCardProps =
  | FilledOrCompletedStatus
  | UnfilledStatus
  | CompletedStatus
  | CancelledStatus
  | WaitListStatus
  | PendingStatus;

/**
 * Component | SubInfoCard
 *
 * Renders a card showing the Sub status associated to a Request. These statuses
 * can be one of the following:
 *
 * - FILLED: Represents a Request which is NOT cancelled, DOES have a Sub
 * assigned and that IS NOT passed it's start date.
 * - UNFILLED: Represents a Request which is NOT cancelled, DOES NOT have a Sub
 * assigned and is NOT passed it's start date.
 * - COMPLETED: Represents a Request which is NOT cancelled, DOES have a Sub
 * assigned and that IS passed it's start date.
 * - CANCELLED: Represents a Request which has been cancelled and thus no Subs
 * will be able to sign up.
 * - WAITLIST: Represents a Long Term Request that subs have signed up for but
 * one has not yet been chosen / accepted for the request
 * - PENDING: Represents a Long Term Request that subs have signed up for and one
 * has been chosen but they have a conflict (signed up for another request that
 * overlaps the dates of this request) but admin has still decided to choose sub
 * and now up to sub to accept. Note: should an admin choose a sub from a wait
 * list and the sub does not have a conflict, then that sub will be automatically
 * accepted for the request and the request will be FILLED
 *
 * @Figma https://www.figma.com/file/GPTogwtUYLEcr9yz9skF5M/Swing-Ionic-Design-System?type=design&node-id=738-26343
 */
export function SubInfoCard(props: SubInfoCardProps) {
  const { status } = props;
  const isChooseSub = "isChooseSub" in props;

  function canReportAnIssue(
    props: SubInfoCardProps,
  ): props is FilledOrCompletedStatus | CancelledStatus {
    return (
      props.status === "FILLED" || props.status === "COMPLETED" || props.status === "CANCELLED"
    );
  }

  return (
    <Card>
      {/* Header */}
      {!isChooseSub && (
        <div
          css={{
            display: "flex",
            flexDirection: "row",
            alignItems: "center",
            gap: 16,
            marginBottom: 16,
          }}
        >
          {/* Title */}
          <h3 css={{ margin: 0, marginTop: 6 }}>{translate("SUB_INFO_CARD_TITLE")}</h3>
          {/* Status */}
          {/* @ts-expect-error TODO: Fix types */}
          <Chip {...props} />
        </div>
      )}

      {/* Body */}
      {(status === "COMPLETED" || status === "FILLED") && (
        // @ts-expect-error TODO: Review why types are not working
        <SubInfoCardFilledOrCompletedBody {...props} />
      )}
      {status === "WAITLIST" && isChooseSub && <SubInfoCardWaitlistChooseSubBody {...props} />}
      {status === "CANCELLED" && <SubInfoCardCancelledBody {...props} />}
      {status === "UNFILLED" && <SubInfoCardUnfilledBody {...props} />}
      {status === "PENDING" && <SubInfoCardPendingBody {...props} />}

      {canReportAnIssue(props) && (
        <AdminExperience
          intervals={props.intervals}
          requestId={props.requestId}
          status={props.status}
          school={props.school}
          subName={props?.sub?.name}
          subId={props?.sub?.id}
          // Make sure `profileImageUrl` and `isSwingHero` exist
          // before accessing to avoid TS errors
          subProfileImgUrl={
            props?.sub && "profileImageUrl" in props.sub ? props.sub.profileImageUrl : undefined
          }
          subIsSwingHero={
            props?.sub && "isSwingHero" in props.sub ? props.sub.isSwingHero : undefined
          }
          hasFeedback={props?.hasFeedback}
          feedback={props?.feedback}
          subLastFeedback={props?.subLastFeedback}
        />
      )}
    </Card>
  );
}

type SwingHeroImageWrapperProps = {
  children: React.ReactNode;
};

export function SwingHeroImageWrapper(props: SwingHeroImageWrapperProps) {
  return (
    <div
      css={{
        display: "inline-block",
        position: "relative",
        width: "fit-content",
        marginLeft: 2,
        border: "1px solid var(--swing-yellow600)",
        padding: 4,
        borderRadius: "50%",
        backgroundColor: "var(--swing-yellow200)",
        marginBottom: 10,
      }}
    >
      {props?.children}
      <p
        css={{
          display: "flex",
          left: "45%",
          bottom: -16,
          margin: 0,
          fontSize: 12,
          alignItems: "center",
          columnGap: 4,
          color: "var(--swing-blue600)",
          fontWeight: 600,
          position: "absolute",
          transform: "translateX(-48%)",
          pointerEvents: "none",
        }}
      >
        {translate("SUB_PROFILE_CONTACT_SWING")}
        <Icon name="Swing Hero" />
        {translate("SUB_PROFILE_CONTACT_HERO")}
      </p>
    </div>
  );
}

/***** Body Components *****/
/**
 * Body Component | SubInfoCardFilledOrCompletedBody
 *
 * Renders the FILLED or COMPLETED status body for the SubInfoCard component.
 *
 * @Figma
 *   - FILLED: https://www.figma.com/file/GPTogwtUYLEcr9yz9skF5M/Swing-Ionic-Design-System?node-id=738%3A26342&mode=dev
 *   - COMPLETED: https://www.figma.com/file/GPTogwtUYLEcr9yz9skF5M/Swing-Ionic-Design-System?node-id=1667%3A886&mode=dev
 */
function SubInfoCardFilledOrCompletedBody(props: FilledOrCompletedStatus) {
  const { sub } = props;
  const { name, profileImageUrl, phoneNumber, email, onSubNameClick, isSwingHero } = sub;

  const parsedPhoneNumber = phoneNumberParser({ phoneNumber: phoneNumber || "" });
  const formattedPhoneNumber = phoneNumberFormatter({
    phoneNumber: parsedPhoneNumber,
  }).value;

  const profileImage = (
    <img
      css={{ width: 92, height: 92, borderRadius: "50%" }}
      src={profileImageUrl || subPhotoPlaceholderPNG}
      alt={name}
    />
  );

  const { userInfo } = useAuth();

  const [present, dismiss] = useIonModal(TxVerifierModal, {
    subId: sub.id,
    dismiss: () => dismiss(),
  });

  return (
    <div css={{ display: "flex", flexDirection: "column", gap: 16 }}>
      {/* Avatar */}
      {isSwingHero ? (
        <SwingHeroImageWrapper>{profileImage}</SwingHeroImageWrapper>
      ) : (
        <>{profileImage}</>
      )}
      {/* Sub Name */}
      {/* TODO: Add to translation table */}
      <IonText className="swing-text">
        {translate("SUB_INFO_CARD_FILLED_LABEL")}
        {/* TODO: Create a Button component which can include a text button */}
        <a
          css={{ textDecoration: "none" }}
          href="#"
          onClick={(e) => {
            e.preventDefault();
            onSubNameClick();
          }}
        >
          {name}
        </a>
      </IonText>
      {/* Sub Phone Number */}
      {formattedPhoneNumber && (
        <IonText className="swing-text" css={{ display: "flex", gap: 6, alignItems: "center" }}>
          <Icon name="Phone" />+{formattedPhoneNumber}
        </IonText>
      )}
      {/* Sub Email */}
      {email && (
        <IonText
          className="swing-text"
          // handle long emails to wrap
          css={{ display: "flex", gap: 6, alignItems: "center", overflowWrap: "anywhere" }}
        >
          <Icon name="Mail" />
          {email}
        </IonText>
      )}
      {/* TX Verifier PII button (compliance - check PDE-3694) */}
      {userInfo.isTexasVerifier && (
        <IonButton
          onClick={() =>
            present({
              cssClass: styles.autoHeightModal,
            })
          }
        >
          {translate("SUB_INFO_CARD_TX_VERIFIER_BUTTON")}
        </IonButton>
      )}
    </div>
  );
}

type TxVerifierModalType = {
  subId: string;
  dismiss: () => void;
};

type PiiResponse = {
  birthDate?: string;
  displayName?: string;
};

const TxVerifierModal: React.FC<TxVerifierModalType> = ({ subId, dismiss }) => {
  const { data, isLoading } = useQuery({
    queryKey: ["piiSubData", subId],
    enabled: !!subId,
    queryFn: async () => {
      const data = await fetchSubPii(subId);
      return data;
    },
  });

  if (isLoading) return;

  const { birthDate, displayName } = data as PiiResponse;

  return (
    <IonPage>
      <IonContent className="ion-padding">
        <div className="flex items-center justify-between w-full">
          <h4>{translate("TX_VERIFIER_NAME")}</h4>
          {!isLoading && <span>{displayName}</span>}
        </div>
        <div className="flex items-center justify-between w-full">
          <h4>{translate("TX_VERIFIER_DOB")}</h4>
          {!isLoading && <span>{birthDate}</span>}
        </div>
        <div className="pt-4 pb-4">{translate("TX_VERIFIER_DESC")}</div>
        <IonButton onClick={dismiss}>{translate("LABEL_DONE")}</IonButton>
      </IonContent>
    </IonPage>
  );
};

/**
 * Body Component | SubInfoCardUnfilledBody
 *
 * Renders the UNFILLED status body for the SubInfoCard component.
 *
 * @Figma https://www.figma.com/file/GPTogwtUYLEcr9yz9skF5M/Swing-Ionic-Design-System?node-id=738%3A26340&mode=dev
 */
function SubInfoCardUnfilledBody(
  // We are defining these `props` for consistency
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  _: UnfilledStatus,
) {
  return (
    <div css={{ display: "flex", flexDirection: "column", gap: 16 }}>
      {/* Avatar */}
      <img
        css={{ width: 128, height: 128 }}
        src="assets/human-binocular.png"
        alt="human with binoculars"
      />
      <IonText className="swing-text">{translate("SUB_INFO_CARD_UNFILLED_MESSAGE")}</IonText>
    </div>
  );
}

/**
 * Body Component | SubInfoCardCancelledBody
 *
 * Renders the CANCELLED status body for the SubInfoCard component.
 *
 * @Figma https://www.figma.com/file/GPTogwtUYLEcr9yz9skF5M/Swing-Ionic-Design-System?node-id=738%3A26341&mode=dev
 */
function SubInfoCardCancelledBody(props: CancelledStatus) {
  const { reason } = props;

  return (
    <>
      <IonText className="swing-text">{translate("SUB_INFO_CARD_CANCELLED_MESSAGE")}</IonText>
      <IonText className="swing-text">
        <span className="font-medium">{translate("CANCEL_REQ_LABEL_CANCELLATION_REASON")}</span>{" "}
        {reason}
      </IonText>
    </>
  );
}

/**
 * Body Component | SubInfoCardWaitlistChooseSubBody
 *
 * Renders the WAITLIST (choose sub) status body for the SubInfoCard component.
 *
 * @Figma https://www.figma.com/file/GPTogwtUYLEcr9yz9skF5M/Swing-Ionic-Design-System?node-id=738%3A26341&mode=dev
 */
function SubInfoCardWaitlistChooseSubBody(props: WaitListStatus) {
  return (
    <>
      <h3 css={{ margin: 0 }}>{translate("WAITLIST")}</h3>
      {props.children}
    </>
  );
}

/**
 * Body Component | SubInfoCardPendingBody
 *
 * Renders the PENDING status body for the SubInfoCard component.
 *
 * @Figma https://www.figma.com/file/GPTogwtUYLEcr9yz9skF5M/Swing-Ionic-Design-System?node-id=738%3A26340&mode=dev
 */
function SubInfoCardPendingBody(props: PendingStatus) {
  const { sub } = props;
  const { name, onSubNameClick } = sub;

  return (
    <>
      <IonText className="swing-text">
        {translate("SUB_INFO_CARD_PENDING_MESSAGE_PRE")}{" "}
        <a
          css={{ textDecoration: "none" }}
          href="#"
          onClick={(e) => {
            e.preventDefault();
            onSubNameClick();
          }}
        >
          {name}
        </a>{" "}
        {translate("SUB_INFO_CARD_PENDING_MESSAGE_POST")}
      </IonText>
    </>
  );
}
